<template>
  <app-dialog
    :model-value="isVisible"
    :title="t('phone_verification.title')"
    title-class="text-xl"
    :description="
      codeSent ? t('phone_verification.code_sent_message') : t('phone_verification.description')
    "
    size="sm"
    :show-close="canDismiss"
    :dismissible="canDismiss"
    @update:model-value="
      (open) => {
        if (!open) handleDismiss();
      }
    "
  >
    <transition name="fade" mode="out-in">
      <!-- Phone Number Input -->
      <div v-if="!codeSent" key="phone-input">
        <app-input
          id="phone-input"
          v-model="phonenumber"
          name="phonenumber"
          autocomplete="tel"
          type="tel"
          :label="t('general_phonenumber')"
          :placeholder="t('phone_verification.phone_placeholder')"
          :disabled="isLoading"
          input-class="text-base"
          @keyup.enter="sendVerificationCode"
        />
        <div v-if="errorMessage" class="mt-2 text-sm text-theme-palette-red dark:text-theme-palette-red">
          {{ errorMessage }}
        </div>
      </div>

      <!-- Verification Code Input -->
      <div v-else key="code-input">
        <app-input
          id="code-input"
          v-model="verificationCode"
          name="verificationCode"
          type="text"
          :label="t('phone_verification.verification_code')"
          :placeholder="t('phone_verification.enter_6_digit_code')"
          maxlength="6"
          :disabled="isVerifying"
          input-class="text-center text-2xl tracking-[0.5em] font-mono"
        />
        <div v-if="errorMessage" class="mt-2 text-sm text-theme-palette-red dark:text-theme-palette-red">
          {{ errorMessage }}
        </div>
        <div v-if="isVerifying" class="mt-2 text-sm text-center text-theme-text-secondary">
          {{ t('phone_verification.verifying') }}
        </div>
      </div>
    </transition>

    <template #footer>
      <div class="flex w-full flex-col gap-3">
        <app-button
          v-if="!codeSent"
          color="emerald"
          :label="t('phone_verification.send_code')"
          :disabled="!phonenumber || isLoading"
          :loading="isLoading"
          class="w-full justify-center"
          @click="sendVerificationCode"
        />
        <app-button
          v-else
          color="light"
          :label="t('phone_verification.resend_verification_code')"
          class="w-full justify-center"
          @click="resetForm"
        />
        <app-button
          v-if="canDismiss"
          color="light"
          :label="t('phone_verification.skip_for_now')"
          class="w-full justify-center"
          @click="handleDismiss"
        />
      </div>
    </template>
  </app-dialog>
</template>

<script setup>
  import { useNotification } from '@/composables/useNotification';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppInput from '@/components/ui/forms/AppInput.vue';
  import AppDialog from '@/components/ui/overlays/AppDialog.vue';
  import { MFA_CODE_LENGTH } from '@/config/domain/mfaCode';
  import AuthRepository from '@/repositories/AuthRepository';
  import { useAuthStore } from '@/stores/useAuthStore';

  const { t } = useI18n();
  const authStore = useAuthStore();
  const { notifySuccess } = useNotification();

  const isVisible = ref(false);
  const codeSent = ref(false);
  const phonenumber = ref('');
  const verificationCode = ref('');
  const isLoading = ref(false);
  const isVerifying = ref(false);
  const errorMessage = ref(null);
  const canDismiss = ref(true);
  const checkDelay = 2000; // 2 second delay before checking

  // Phone verification is opt-in only (a 2FA method the user chooses), never a
  // login-time nudge. The auto-prompt stays OFF unless explicitly enabled via the
  // VUE_APP_ENABLE_PHONE_VERIFICATION_PROMPT build flag, so an unset env var can
  // never silently re-enable it.
  const isPhoneVerificationPromptEnabled =
    process.env.VUE_APP_ENABLE_PHONE_VERIFICATION_PROMPT === 'true';

  // Get initial phone number from store if available
  const getPhonenumber = () => authStore.phonenumber;

  // Watch for user authentication and check phone verification status
  const isAuthenticated = computed(() => authStore.isAuthenticated);
  const hasVerifiedPhone = computed(() => authStore.has_verified_phone_number);
  const userId = computed(() => authStore.id);
  const isUserLoaded = computed(() => authStore.isUserLoaded);

  let checkTimeout = null;

  // Use function declarations (hoisted) for functions used in immediate watchers
  function showModal() {
    isVisible.value = true;
    phonenumber.value = getPhonenumber() || '';
  }

  function hideModal() {
    isVisible.value = false;
  }

  function resetForm() {
    codeSent.value = false;
    verificationCode.value = '';
    errorMessage.value = null;
  }

  function handleBackdropClick() {
    if (canDismiss.value) {
      handleDismiss();
    }
  }

  function handleDismiss() {
    hideModal();
    resetForm();
  }

  // Set up watchers after function definitions
  watch(getPhonenumber, (newPhonenumber) => {
    if (newPhonenumber) {
      phonenumber.value = newPhonenumber;
    }
  });

  watch(
    [isAuthenticated, hasVerifiedPhone, userId, isUserLoaded],
    ([authenticated, verified, id, userLoaded]) => {
      // Clear any pending timeout
      if (checkTimeout) {
        clearTimeout(checkTimeout);
        checkTimeout = null;
      }

      // Opt-in only: never auto-surface the modal unless the prompt is explicitly enabled.
      if (!isPhoneVerificationPromptEnabled) {
        hideModal();
        return;
      }

      // Only show modal after user data is fully loaded
      if (authenticated && id && !verified && userLoaded) {
        // Add delay before showing the modal
        checkTimeout = setTimeout(() => {
          showModal();
        }, checkDelay);
      } else {
        hideModal();
      }
    },
    { immediate: true }
  );

  // Auto-verify when code is complete
  watch(
    () => verificationCode.value,
    (newCode) => {
      if (newCode.length === MFA_CODE_LENGTH) {
        verifyCode();
      }
    }
  );

  const sendVerificationCode = async () => {
    if (!phonenumber.value || isLoading.value) return;

    isLoading.value = true;
    errorMessage.value = null;

    try {
      await AuthRepository.sendSmsVerification({
        phonenumber: phonenumber.value,
      });

      // Update store with phone number
      authStore.setPhoneNumber(phonenumber.value);

      codeSent.value = true;
    } catch (error) {
      if (error.response?.status === 422) {
        errorMessage.value = error.response.data.message;
      } else {
        errorMessage.value = t('phone_verification.error_sending_code');
      }
    } finally {
      isLoading.value = false;
    }
  };

  const verifyCode = async () => {
    if (isVerifying.value || verificationCode.value.length !== MFA_CODE_LENGTH) return;

    isVerifying.value = true;
    errorMessage.value = null;

    try {
      await AuthRepository.verifyPhone({
        code: verificationCode.value,
      });

      // Update store to mark phone as verified
      authStore.setHasVerifiedPhoneNumber(true);

      // Show success message
      notifySuccess(t('phone_verification.success'));

      // Close modal
      hideModal();
      resetForm();
    } catch (error) {
      verificationCode.value = '';

      if (error.response?.status === 422) {
        errorMessage.value = error.response.data.message;
      } else {
        errorMessage.value = t('phone_verification.error_verifying_code');
      }
    } finally {
      isVerifying.value = false;
    }
  };
</script>

<style scoped>
  .modal-enter-active,
  .modal-leave-active {
    transition: opacity 0.3s ease;
  }

  .modal-enter-from,
  .modal-leave-to {
    opacity: 0;
  }

  .modal-enter-active .relative,
  .modal-leave-active .relative {
    transition: transform 0.3s ease, opacity 0.3s ease;
  }

  .modal-enter-from .relative,
  .modal-leave-to .relative {
    transform: scale(0.95);
    opacity: 0;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
